import React, { useState, useEffect, useContext, useMemo, useCallback, useRef, useId } from 'react';
import Select from 'react-select';
import JSZip from 'jszip';
import styles from './VariantsForm.module.css';
import AuthContext from '../../../context/AuthContext';
import {
  RemoveBackgroundModal,
  DeleteImagesModal,
  DeleteConfirmationModal,
  RestoreImagesModal
} from './ImageModals';

function ChevronDropdownIndicator() {
  return (
    <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 8px', flexShrink: 0 }}>
      <path d="M1.7625 0L7.5 5.56275L13.2375 0L15 1.71255L7.5 9L0 1.71255L1.7625 0Z" fill="#737373"/>
    </svg>
  );
}

function AddButtonIcon() {
  const id = useId().replace(/:/g, '');
  const gradientId = `paint0_linear_${id}`;
  return (
    <span className={styles.addButtonIcon}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 0.299805C8.71786 0.299805 9.29959 0.881823 9.2998 1.59961V6.7002H14.4004C15.1182 6.70041 15.7002 7.28213 15.7002 8C15.7002 8.71786 15.1182 9.29959 14.4004 9.2998H9.2998V14.4004C9.29959 15.1182 8.71786 15.7002 8 15.7002C7.28214 15.7002 6.70041 15.1182 6.7002 14.4004V9.2998H1.59961C0.881823 9.29959 0.299805 8.71786 0.299805 8C0.299805 7.28213 0.881821 6.70041 1.59961 6.7002H6.7002V1.59961C6.70041 0.881822 7.28214 0.299805 8 0.299805Z" fill={`url(#${gradientId})`} stroke="#F5FAFF" strokeWidth="0.6"/>
        <defs>
          <linearGradient id={gradientId} x1="-5.96046e-08" y1="8" x2="14.6857" y2="16" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2766AA"/>
            <stop offset="1" stopColor="#0752B0"/>
          </linearGradient>
        </defs>
      </svg>
    </span>
  );
}

// Parse draft data into level1 and variants (used for initial state and hydration when returning to tab)
function parseDraftLevel1(d) {
  if (d?.level1Values && Array.isArray(d.level1Values)) return d.level1Values;
  return [''];
}
function parseDraftVariants(d) {
  if (!d) return [[{ level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} }]];
  const l1 = (d?.level1Values && Array.isArray(d.level1Values)) ? d.level1Values : [''];
  const raw = d?.variants;
  const normalizeLevel3Fields = (level3, sku, images) => {
    if (Array.isArray(level3) && level3.length > 0 && typeof level3[0] === 'object' && level3[0].value !== undefined) return level3;
    const level3Array = Array.isArray(level3) ? level3 : (level3 ? [level3] : ['']);
    return level3Array.map((val, idx) => ({
      value: val || '',
      sku: idx === 0 ? (sku || '') : '',
      images: idx === 0 ? (images?.level3 || []) : []
    }));
  };
  if (Array.isArray(raw)) {
    if (raw.length === 0) return [[{ level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} }]];
    return raw.map(row => row.map(v => {
      const level3Fields = normalizeLevel3Fields(v.level3Fields ?? v.level3, v.sku, v.images);
      return { ...v, level3Fields, level3: undefined };
    }));
  }
  if (raw && typeof raw === 'object') {
    return l1.map(v => {
      const row = raw[v] || [{ level2: '', level3: '', sku: '', images: {} }];
      return row.map(variant => {
        const level3Fields = normalizeLevel3Fields(variant.level3Fields ?? variant.level3, variant.sku, variant.images);
        return { ...variant, level3Fields, level3: undefined };
      });
    });
  }
  return [[{ level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} }]];
}

const VariantsStep2 = ({ formData, handleChange, isLoading, variantsDraftData, setVariantsDraftData, onValidationChange, variantType: variantTypeProp, onBackToStep1 }) => {
  const authCtx = useContext(AuthContext);
  const variantType = variantTypeProp || 'Color/Size/Pack';

  const attributeEnums = formData?.productTypeAttributeEnums ?? {};
  const getAllowedValues = (levelType) => {
    if (!levelType || !attributeEnums) return null;
    const key = levelType.trim().toLowerCase().replace(/\s+/g, '_');
    return attributeEnums[key] ?? null;
  };
  const enumSelectStyles = {
    container: (p) => ({ ...p, width: '100%' }),
    control: (p) => ({ ...p, border: 'none', boxShadow: 'none', background: 'transparent', minHeight: 36, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif" }),
    menu: (p) => ({ ...p, zIndex: 1000, fontSize: 14 }),
    placeholder: (p) => ({ ...p, color: '#898989' }),
  };
  const renderEnumOrTextInput = (value, onChangeValue, className, levelType, placeholder) => {
    const allowed = getAllowedValues(levelType);
    if (!allowed) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChangeValue(e.target.value)}
          className={className}
          placeholder={placeholder}
          disabled={isLoading}
        />
      );
    }
    return (
      <div className={className} style={{ padding: 0, display: 'flex', alignItems: 'center' }}>
        <Select
          options={allowed.map((v) => ({ value: v, label: String(v) }))}
          onChange={(opt) => onChangeValue(opt?.value ?? '')}
          value={value ? { value, label: value } : null}
          isSearchable
          isDisabled={isLoading}
          placeholder={placeholder}
          classNamePrefix="react-select"
          components={{ DropdownIndicator: ChevronDropdownIndicator, IndicatorSeparator: () => null }}
          styles={enumSelectStyles}
        />
      </div>
    );
  };
  
  const getInitialLevel1 = () => parseDraftLevel1(variantsDraftData);
  const getInitialVariants = () => parseDraftVariants(variantsDraftData);
  const [level1Values, setLevel1Values] = useState(getInitialLevel1);
  const [variants, setVariants] = useState(getInitialVariants);

  // Hydrate from draft when returning to Variants tab (component remounts with existing variantsDraftData)
  const hasHydratedFromDraft = useRef(false);
  useEffect(() => {
    if (hasHydratedFromDraft.current) return;
    const d = variantsDraftData;
    if (!d) return;
    const hasLevel1 = d.level1Values && Array.isArray(d.level1Values) && d.level1Values.length > 0;
    const hasVariants = d.variants && Array.isArray(d.variants) && d.variants.length > 0;
    if (!hasLevel1 && !hasVariants) return;
    hasHydratedFromDraft.current = true;
    if (d.level1Values && Array.isArray(d.level1Values)) setLevel1Values(d.level1Values);
    if (d.variants && Array.isArray(d.variants)) setVariants(parseDraftVariants(d));
  }, [variantsDraftData]);

  // State for image management modals
  const [removeBgModal, setRemoveBgModal] = useState(false);
  const [selectedImagesForBgRemoval, setSelectedImagesForBgRemoval] = useState(new Set());
  const [processingBgRemoval, setProcessingBgRemoval] = useState(false);
  const [deleteImagesModal, setDeleteImagesModal] = useState(false);
  const [selectedImagesForDeletion, setSelectedImagesForDeletion] = useState(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletedImages, setDeletedImages] = useState([]);
  const [restoreImagesModal, setRestoreImagesModal] = useState(false);
  const [selectedImagesForRestore, setSelectedImagesForRestore] = useState(new Set());

  // Refs to track values for cleanup on unmount
  const deletedImagesRef = useRef([]);
  const variantsRef = useRef(variants);
  const bulkZipInputRef = useRef(null);

  // Parse variant type to get ordered levels
  const getLevels = () => {
    return variantType.split('/').map(level => level.trim());
  };

  const levels = getLevels();
  const level1Type = levels[0] || '';
  const level2Type = levels[1] || '';
  const level3Type = levels[2] || '';

  // Helper functions to determine which levels to show
  const showLevel1 = () => level1Type !== '';
  const showLevel2 = () => level2Type !== '';
  const showLevel3 = () => level3Type !== '';

  // Validation function to check if all fields are filled and images are uploaded
  const validateVariants = useCallback(() => {
    // Ensure at least one variant exists
    if (variants.length === 0) {
      return false;
    }

    // Check Level1 fields if Level1 is shown
    if (showLevel1()) {
      if (level1Values.length === 0) {
        return false;
      }
      for (let i = 0; i < level1Values.length; i++) {
        if (!level1Values[i]?.trim()) {
          return false;
        }
      }
    }

    // Check Level2, Level3, SKU, and images for each variant
    for (let level1Index = 0; level1Index < variants.length; level1Index++) {
      const level2Variants = variants[level1Index] || [];
      
      // Ensure at least one Level2 variant exists
      if (level2Variants.length === 0) {
        return false;
      }
      
      for (let level2Index = 0; level2Index < level2Variants.length; level2Index++) {
        const variant = level2Variants[level2Index];
        
        // Check Level2 field if Level2 is shown
        if (showLevel2() && !variant.level2?.trim()) {
          return false;
        }
        
        // Check Level3 fields if Level3 is shown
        if (showLevel3()) {
          const level3Fields = Array.isArray(variant.level3Fields) ? variant.level3Fields : [];
          if (level3Fields.length === 0) {
            return false;
          }
          
          for (let level3Index = 0; level3Index < level3Fields.length; level3Index++) {
            const level3Field = level3Fields[level3Index];
            
            // Check Level3 value
            if (!level3Field?.value?.trim()) {
              return false;
            }
            
            // Check SKU for Level3 field
            if (!level3Field?.sku?.trim()) {
              return false;
            }
            
            // Check images for Level3 field
            const level3Images = Array.isArray(level3Field.images) ? level3Field.images : [];
            if (level3Images.length === 0) {
              return false;
            }
          }
        } else {
          // If no Level3, check Level2 SKU and images
          if (!variant.sku?.trim()) {
            return false;
          }
          
          const level2Images = Array.isArray(variant.images?.level2) ? variant.images.level2 : [];
          if (level2Images.length === 0) {
            return false;
          }
        }
      }
    }
    
    return true;
  }, [level1Values, variants, level1Type, level2Type, level3Type]);

  const addLevel1Variant = () => {
    setLevel1Values((prev) => [...prev, '']);
    setVariants((prev) => [...prev, [{ level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} }]]);
  };

  const updateLevel1 = (index, value) => {
    setLevel1Values((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const removeLevel1 = (index) => {
    setLevel1Values((prev) => prev.filter((_, i) => i !== index));
    setVariants((prev) => {
      const row = prev[index] || [];
      row.forEach((variant) => {
        if (variant?.images) {
          Object.values(variant.images).forEach(imageArray => {
            if (Array.isArray(imageArray)) {
              imageArray.forEach(obj => {
                const u = obj?.previewUrl;
                if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
              });
            }
          });
        }
      });
      return prev.filter((_, i) => i !== index);
    });
  };

  const addLevel2Variant = (level1Index) => {
    setVariants((prev) => {
      const current = prev[level1Index] || [];
      if (current.length >= 5) return prev;
      const next = [...prev];
      next[level1Index] = [...current, { level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} }];
      return next;
    });
  };

  const updateLevel2 = (level1Index, variantIndex, value) => {
    setVariants((prev) => {
      const next = prev.map((row, i) => {
        if (i !== level1Index || !row[variantIndex]) return row;
        const nextRow = [...row];
        nextRow[variantIndex] = { ...nextRow[variantIndex], level2: value };
        return nextRow;
      });
      return next;
    });
  };

  const updateLevel3Field = (level1Index, variantIndex, level3Index, value) => {
    setVariants((prev) => {
      const next = prev.map((row, i) => {
        if (i !== level1Index || !row[variantIndex]) return row;
        const nextRow = [...row];
        const variant = nextRow[variantIndex];
        const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [{ value: '', sku: '', images: [] }];
        level3Fields[level3Index] = { ...level3Fields[level3Index], value };
        nextRow[variantIndex] = { ...variant, level3Fields };
        return nextRow;
      });
      return next;
    });
  };

  const updateLevel3Sku = (level1Index, variantIndex, level3Index, value) => {
    setVariants((prev) => {
      const next = prev.map((row, i) => {
        if (i !== level1Index || !row[variantIndex]) return row;
        const nextRow = [...row];
        const variant = nextRow[variantIndex];
        const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [{ value: '', sku: '', images: [] }];
        level3Fields[level3Index] = { ...level3Fields[level3Index], sku: value };
        nextRow[variantIndex] = { ...variant, level3Fields };
        return nextRow;
      });
      return next;
    });
  };

  const addLevel3Field = (level1Index, variantIndex) => {
    setVariants((prev) => {
      const next = prev.map((row, i) => {
        if (i !== level1Index || !row[variantIndex]) return row;
        const nextRow = [...row];
        const variant = nextRow[variantIndex];
        const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [{ value: '', sku: '', images: [] }];
        if (level3Fields.length >= 5) return row; // Max 5 fields
        level3Fields.push({ value: '', sku: '', images: [] });
        nextRow[variantIndex] = { ...variant, level3Fields };
        return nextRow;
      });
      return next;
    });
  };

  const removeLevel3Field = (level1Index, variantIndex, level3Index) => {
    setVariants((prev) => {
      const next = prev.map((row, i) => {
        if (i !== level1Index || !row[variantIndex]) return row;
        const nextRow = [...row];
        const variant = nextRow[variantIndex];
        const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [{ value: '', sku: '', images: [] }];
        if (level3Fields.length <= 1) return row; // Keep at least 1 field
        const fieldToRemove = level3Fields[level3Index];
        // Clean up images for this level3 field
        if (fieldToRemove?.images && Array.isArray(fieldToRemove.images)) {
          fieldToRemove.images.forEach(imgObj => {
            const u = imgObj?.previewUrl;
            if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
          });
        }
        const newLevel3Fields = level3Fields.filter((_, idx) => idx !== level3Index);
        nextRow[variantIndex] = { ...variant, level3Fields: newLevel3Fields };
        return nextRow;
      });
      return next;
    });
  };

  const removeLevel2Variant = (level1Index, index) => {
    setVariants((prev) => {
      const row = prev[level1Index]?.[index];
      if (row) {
        // Clean up level2 images
        if (row.images) {
          Object.values(row.images).forEach(imageArray => {
            if (Array.isArray(imageArray)) {
              imageArray.forEach(obj => {
                const u = obj?.previewUrl;
                if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
              });
            }
          });
        }
        // Clean up level3Fields images
        if (row.level3Fields && Array.isArray(row.level3Fields)) {
          row.level3Fields.forEach(level3Field => {
            if (level3Field?.images && Array.isArray(level3Field.images)) {
              level3Field.images.forEach(obj => {
                const u = obj?.previewUrl;
                if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
              });
            }
          });
        }
      }
      const next = [...prev];
      next[level1Index] = (next[level1Index] || []).filter((_, i) => i !== index);
      return next;
    });
  };

  const updateSku = (level1Index, variantIndex, value) => {
    setVariants((prev) => {
      const next = prev.map((row, i) => {
        if (i !== level1Index || !row[variantIndex]) return row;
        const nextRow = [...row];
        nextRow[variantIndex] = { ...nextRow[variantIndex], sku: value };
        return nextRow;
      });
      return next;
    });
  };

  const handleImageUpload = (level1Index, variantIndex, level3Index, event) => {
    const files = Array.from(event.target.files || []);
    const isLevel3 = level3Index !== undefined;

    setVariants((prev) => {
      const current = prev[level1Index]?.[variantIndex];
      if (!current) return prev;
      
      if (isLevel3) {
        // Handle level3Fields images
        const level3Fields = Array.isArray(current.level3Fields) ? [...current.level3Fields] : [{ value: '', sku: '', images: [] }];
        const level3Field = level3Fields[level3Index] || { value: '', sku: '', images: [] };
        const existingImages = Array.isArray(level3Field.images) ? level3Field.images : [];
        const remainingSlots = 5 - existingImages.length;
        const filesToAdd = files.slice(0, remainingSlots);
        const newImageObjects = filesToAdd.map(file => ({ file, previewUrl: URL.createObjectURL(file) }));
        const nextImages = [...existingImages, ...newImageObjects];
        level3Fields[level3Index] = { ...level3Field, images: nextImages };
        const newRow = { ...current, level3Fields };
        const next = [...prev];
        const nextRow = [...(next[level1Index] || [])];
        nextRow[variantIndex] = newRow;
        next[level1Index] = nextRow;
        return next;
      } else {
        // Handle level2 images (existing logic)
        const imageKey = 'level2';
        const existingImages = (current.images || {})[imageKey] || [];
        const remainingSlots = 5 - existingImages.length;
        const filesToAdd = files.slice(0, remainingSlots);
        const newImageObjects = filesToAdd.map(file => ({ file, previewUrl: URL.createObjectURL(file) }));
        const nextImages = [...existingImages, ...newImageObjects];
        const newRow = {
          ...current,
          images: { ...(current.images || {}), [imageKey]: nextImages }
        };
        const next = [...prev];
        const nextRow = [...(next[level1Index] || [])];
        nextRow[variantIndex] = newRow;
        next[level1Index] = nextRow;
        return next;
      }
    });
    event.target.value = '';
  };

  // Shared render helpers to reduce repetition
  const renderImageUpload = (variant, level1Index, level2Index, level3Index, idPrefix) => {
    let imagesForKey = [];
    if (level3Index !== undefined) {
      // Get images from level3Fields
      const level3Fields = Array.isArray(variant.level3Fields) ? variant.level3Fields : [];
      const level3Field = level3Fields[level3Index];
      imagesForKey = Array.isArray(level3Field?.images) ? level3Field.images : [];
    } else {
      // Get images from level2
      imagesForKey = variant.images?.level2 || [];
    }
    const inputId = `${idPrefix}-${level2Index}${level3Index !== undefined ? `-level3-${level3Index}` : ''}`;
    const isDisabled = isLoading || imagesForKey.length >= 5;
    const tooltipText = imagesForKey.length >= 5 ? "Upload upto 5 images only" : "Upload images";

    return (
      <div className={styles.imageUploadContainer}>
        <input
          type="file"
          id={isDisabled ? undefined : inputId}
          accept="image/*"
          multiple
          onChange={(e) => handleImageUpload(level1Index, level2Index, level3Index, e)}
          disabled={isLoading}
          className={styles.imageFileInput}
        />
        {imagesForKey.length > 0 && (
          <div className={styles.imageThumbnails}>
            {imagesForKey.map((imageObj, imgIndex) => (
              <div key={imgIndex} className={styles.imageThumbnail}>
                <img src={(imageObj?.previewUrl || imageObj)} alt={`Upload ${imgIndex + 1}`} />
                <div className={styles.imageThumbnailOverlay}>
                  <button
                    type="button"
                    className={styles.imageThumbnailDelete}
                    onClick={(e) => { e.preventDefault(); removeImage(level1Index, level2Index, level3Index, imgIndex); }}
                    title="Remove image"
                    disabled={isLoading}
                  >
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="27.5" height="27.5" rx="13.75" fill="#DE524A"/>
                      <path d="M20.5606 8.85553H18.5055L18.0977 7.22349C17.8367 6.17882 16.9022 5.4502 15.8264 5.4502H11.675C10.5992 5.4502 9.66546 6.17882 9.40372 7.22349L8.99591 8.85553H6.94021C6.58746 8.85553 6.30176 9.14123 6.30176 9.49398C6.30176 9.84673 6.58746 10.1324 6.94021 10.1324H7.19718L7.89228 19.8759C7.97927 21.0945 9.00478 22.0499 10.2275 22.0499H17.2719C18.4945 22.0499 19.52 21.0946 19.6071 19.8759L20.3022 10.1324H20.5591C20.9119 10.1324 21.1976 9.84673 21.1976 9.49398C21.1976 9.14123 20.9119 8.85553 20.5591 8.85553H20.5606ZM10.6431 7.53313C10.762 7.05829 11.1865 6.72709 11.6757 6.72709H15.8272C16.3164 6.72709 16.741 7.05909 16.8599 7.53313L17.1903 8.85553H10.3118L10.6431 7.53313ZM18.3347 19.785C18.2956 20.3389 17.8287 20.773 17.2733 20.773H10.228C9.67179 20.773 9.20573 20.3389 9.16663 19.785L8.4771 10.1325H19.0235L18.3347 19.785ZM14.8145 17.1554V12.8994C14.8145 12.5466 15.1002 12.2609 15.453 12.2609C15.8057 12.2609 16.0914 12.5466 16.0914 12.8994V17.1554C16.0914 17.5082 15.8057 17.7939 15.453 17.7939C15.1002 17.7939 14.8145 17.5082 14.8145 17.1554ZM11.41 17.1554V12.8994C11.41 12.5466 11.6957 12.2609 12.0485 12.2609C12.4012 12.2609 12.6869 12.5466 12.6869 12.8994V17.1554C12.6869 17.5082 12.4012 17.7939 12.0485 17.7939C11.6957 17.7939 11.41 17.5082 11.41 17.1554Z" fill="white"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <label
          htmlFor={isDisabled ? undefined : inputId}
          aria-disabled={isDisabled}
          className={`${styles.imageUploadButton} ${isDisabled ? styles.imageUploadButtonDisabled : ''} ${imagesForKey.length === 0 ? styles.imageUploadButtonTextOnly : ''}`}
          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.6 : 1 }}
          data-tooltip={imagesForKey.length === 0 ? '' : tooltipText}
        >
          {imagesForKey.length === 0 ? (
            <span className={styles.imageUploadButtonLabel}>Upload Images</span>
          ) : (
            <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="31" height="31" rx="15.5" fill="#78B3F3"/>
              <path d="M15.4451 19.5557C15.8557 19.5557 16.188 19.2444 16.188 18.8607V10.8645L19.6222 14.0777C19.7673 14.2135 19.9574 14.2811 20.1476 14.2811C20.3378 14.2811 20.528 14.2135 20.6731 14.0777C20.9633 13.8062 20.9633 13.3665 20.6731 13.095L15.9706 8.69564C15.6918 8.43479 15.1985 8.43479 14.9202 8.69564L10.2177 13.095C9.92744 13.3665 9.92744 13.8062 10.2177 14.0777C10.5079 14.3492 10.9779 14.3492 11.2681 14.0777L14.7022 10.8645V18.8607C14.7022 19.2444 15.0346 19.5557 15.4451 19.5557Z" fill="white"/>
              <path d="M9.24289 17.8884C8.83232 17.8884 8.5 18.1998 8.5 18.5834V21.805C8.5 22.1886 8.83232 22.5 9.24289 22.5H21.7571C22.1677 22.5 22.5 22.1886 22.5 21.805V18.5834C22.5 18.1998 22.1677 17.8884 21.7571 17.8884C21.3465 17.8884 21.0142 18.1998 21.0142 18.5834V21.11H9.98578V18.5834C9.98578 18.1998 9.65346 17.8884 9.24289 17.8884Z" fill="white"/>
            </svg>
          )}
        </label>
      </div>
    );
  };

  const renderLevel3Fields = (level1Index, variant, level2Index, level3Type) => {
    const level3Fields = Array.isArray(variant.level3Fields) ? variant.level3Fields : [{ value: '', sku: '', images: [] }];
    const canAddMore = level3Fields.length < 5;
    const canRemove = level3Fields.length > 1;
    const lastIndex = level3Fields.length - 1;

    return (
      <div className={styles.level3FieldsContainer}>
        {level3Fields.map((level3Field, level3Index) => {
          const isLast = level3Index === lastIndex;
          return (
            <div key={level3Index} className={styles.level3FieldRow}>
              <div className={styles.level3FieldWrapper}>
                {renderEnumOrTextInput(
                  level3Field?.value || '',
                  (v) => updateLevel3Field(level1Index, level2Index, level3Index, v),
                  styles.level3Input,
                  level3Type,
                  level3Type || 'Variant Level 3'
                )}
              </div>
              <input
                type="text"
                value={level3Field?.sku || ''}
                onChange={(e) => updateLevel3Sku(level1Index, level2Index, level3Index, e.target.value)}
                className={styles.skuInput}
                placeholder="SKU ID"
                disabled={isLoading}
              />
              {renderImageUpload(variant, level1Index, level2Index, level3Index, `image-upload-l1-${level1Index}-l2-${level2Index}`)}
              {canRemove && (
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => removeLevel3Field(level1Index, level2Index, level3Index)}
                  disabled={isLoading}
                  style={{ marginLeft: '6px' }}
                >
                  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5169 3.46697H12.4246L12.0094 1.80539C11.7437 0.741818 10.7923 0 9.69707 0H5.47047C4.37522 0 3.42458 0.741818 3.15811 1.80539L2.74292 3.46697H0.650006C0.29087 3.46697 0 3.75784 0 4.11698C0 4.47612 0.29087 4.76699 0.650006 4.76699H0.911631L1.61932 14.6868C1.70788 15.9275 2.75195 16.9002 3.99678 16.9002H11.1687C12.4134 16.9002 13.4575 15.9276 13.5461 14.6868L14.2538 4.76699H14.5155C14.8746 4.76699 15.1655 4.47612 15.1655 4.11698C15.1655 3.75784 14.8746 3.46697 14.5155 3.46697H14.5169ZM4.41987 2.12064C4.54094 1.6372 4.9732 1.30001 5.47126 1.30001H9.69786C10.1959 1.30001 10.6282 1.63801 10.7492 2.12064L11.0856 3.46697H4.08262L4.419 2.12064H4.41987ZM12.2507 14.5943C12.2109 15.1582 11.7356 15.6002 11.1701 15.6002H3.99736C3.43103 15.6002 2.95654 15.1582 2.91673 14.5943L2.21472 4.76705H12.952L12.25 14.5943H12.2507ZM8.66686 11.9171V7.58402C8.66686 7.22488 8.95773 6.93401 9.31686 6.93401C9.676 6.93401 9.96687 7.22488 9.96687 7.58402V11.9171C9.96687 12.2762 9.676 12.5671 9.31686 12.5671C8.95773 12.5671 8.66686 12.2762 8.66686 11.9171ZM5.20071 11.9171V7.58402C5.20071 7.22488 5.49158 6.93401 5.85072 6.93401C6.20985 6.93401 6.50072 7.22488 6.50072 7.58402V11.9171C6.50072 12.2762 6.20985 12.5671 5.85072 12.5671C5.49158 12.5671 5.20071 12.2762 5.20071 11.9171Z" fill="#737373"/>
                  </svg>
                </button>
              )}
            </div>
          );
        })}
        {canAddMore && (
          <div className={styles.addButtonBelowField}>
            <button
              type="button"
              className={styles.addLevel1Button}
              onClick={() => addLevel3Field(level1Index, level2Index)}
              disabled={isLoading}
              data-tooltip={`Add ${level3Type || "Level 3"}`}
            >
              <AddButtonIcon /> Add {level3Type || "Variant Level 3"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const removeImage = (level1Index, variantIndex, level3Index, imageIndex) => {
    // Capture the image before removing it from variants
    const row = variants[level1Index]?.[variantIndex];
    if (!row) return;

    let image;
    let imageKey;
    if (level3Index !== undefined) {
      const level3Fields = Array.isArray(row.level3Fields) ? row.level3Fields : [];
      image = level3Fields[level3Index]?.images?.[imageIndex];
      imageKey = 'level3';
    } else {
      image = row.images?.level2?.[imageIndex];
      imageKey = 'level2';
    }

    if (!image) return;

    // Add to deleted images for restore
    const timestamp = Date.now();
    const deletedEntry = {
      image,
      level1Index,
      variantIndex,
      imageKey,
      imageIndex,
      level3Index: level3Index !== undefined ? level3Index : undefined,
      deletedAt: timestamp,
      uniqueId: `deleted-${level1Index}-${variantIndex}-${imageKey}-${imageIndex}-${level3Index !== undefined ? `l3-${level3Index}-` : ''}${timestamp}-0-${Math.random().toString(36).substr(2, 9)}`
    };
    setDeletedImages(prev => {
      const updated = [...prev, deletedEntry];
      deletedImagesRef.current = updated;
      return updated;
    });

    // Remove image from variants
    setVariants((prev) => {
      const currentRow = prev[level1Index]?.[variantIndex];
      if (!currentRow) return prev;

      if (level3Index !== undefined) {
        const level3Fields = Array.isArray(currentRow.level3Fields) ? [...currentRow.level3Fields] : [];
        const level3Field = level3Fields[level3Index];
        if (!level3Field?.images?.[imageIndex]) return prev;
        level3Fields[level3Index] = {
          ...level3Field,
          images: level3Field.images.filter((_, j) => j !== imageIndex)
        };
        const next = [...prev];
        const nextRow = [...(next[level1Index] || [])];
        nextRow[variantIndex] = { ...currentRow, level3Fields };
        next[level1Index] = nextRow;
        return next;
      } else {
        const ik = 'level2';
        if (!currentRow?.images?.[ik]?.[imageIndex]) return prev;
        const next = [...prev];
        const nextRow = [...(next[level1Index] || [])];
        nextRow[variantIndex] = {
          ...nextRow[variantIndex],
          images: {
            ...(nextRow[variantIndex].images || {}),
            [ik]: (nextRow[variantIndex].images[ik] || []).filter((_, j) => j !== imageIndex)
          }
        };
        next[level1Index] = nextRow;
        return next;
      }
    });
  };

  // Build map: SKU string -> { level1Index, variantIndex, level3Index? } (level3Index undefined = level2 images)
  const buildSkuToSlot = useCallback((variantGrid) => {
    const map = {};
    (variantGrid || []).forEach((row, level1Index) => {
      (row || []).forEach((variant, variantIndex) => {
        const level3Fields = Array.isArray(variant.level3Fields) ? variant.level3Fields : [];
        const hasLevel3Skus = level3Fields.some(l3 => (l3?.sku || '').toString().trim() !== '');
        if (hasLevel3Skus) {
          level3Fields.forEach((l3, level3Index) => {
            const sku = (l3?.sku || '').toString().trim();
            if (sku && !map[sku]) map[sku] = { level1Index, variantIndex, level3Index };
          });
        } else {
          const sku = (variant?.sku || '').toString().trim();
          if (sku && !map[sku]) map[sku] = { level1Index, variantIndex, level3Index: undefined };
        }
      });
    });
    return map;
  }, []);

  const handleBulkZipUpload = useCallback(async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = '';
    if (!file || !file.name.toLowerCase().endsWith('.zip')) return;
    try {
      const zip = await JSZip.loadAsync(file);
      const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
      const folderMap = {};
      zip.forEach((relativePath, zipEntry) => {
        if (zipEntry.dir) return;
        const parts = relativePath.split('/').filter(Boolean);
        if (parts.length < 2) return;
        const folderName = parts[parts.length - 2];
        const fileName = parts[parts.length - 1];
        const ext = fileName.split('.').pop().toLowerCase();
        if (!IMAGE_EXT.includes(ext)) return;
        if (relativePath.includes('__MACOSX') || fileName.startsWith('._') || fileName.startsWith('.')) return;
        if (!folderMap[folderName]) folderMap[folderName] = [];
        if (folderMap[folderName].length < 5) folderMap[folderName].push(zipEntry);
      });
      const skuToNewImages = {};
      for (const sku of Object.keys(folderMap)) {
        const entries = folderMap[sku];
        const blobs = await Promise.all(entries.map(entry => entry.async('blob')));
        const imageObjs = blobs.map((blob, i) => {
          const f = new File([blob], `${sku}-${i}.jpg`, { type: blob.type || 'image/jpeg' });
          return { file: f, previewUrl: URL.createObjectURL(f) };
        });
        skuToNewImages[sku.trim()] = imageObjs;
      }
      setVariants((prev) => {
        const skuToSlot = buildSkuToSlot(prev);
        const next = prev.map(row => [...(row || [])]);
        for (const [sku, imageObjs] of Object.entries(skuToNewImages)) {
          const slot = skuToSlot[sku];
          if (!slot) continue;
          const { level1Index, variantIndex, level3Index } = slot;
          const row = next[level1Index]?.[variantIndex];
          if (!row) continue;
          if (level3Index !== undefined) {
            const level3Fields = Array.isArray(row.level3Fields) ? [...row.level3Fields] : [];
            const l3 = level3Fields[level3Index];
            if (!l3) continue;
            const existing = Array.isArray(l3.images) ? l3.images : [];
            const toAdd = imageObjs.slice(0, 5 - existing.length);
            level3Fields[level3Index] = { ...l3, images: [...existing, ...toAdd] };
            next[level1Index][variantIndex] = { ...row, level3Fields };
          } else {
            const imageKey = 'level2';
            const existing = (row.images && row.images[imageKey]) || [];
            const toAdd = imageObjs.slice(0, 5 - existing.length);
            next[level1Index][variantIndex] = {
              ...row,
              images: { ...(row.images || {}), [imageKey]: [...existing, ...toAdd] }
            };
          }
        }
        return next;
      });
    } catch (err) {
      console.error('Bulk ZIP upload failed:', err);
    }
  }, [buildSkuToSlot]);

  // Get unique images from all variants (for Remove Background)
  const uniqueImages = useMemo(() => {
    const out = [];
    const seenUrls = new Set();
    (variants || []).forEach((variantArray, level1Index) => {
      (variantArray || []).forEach((variant, variantIndex) => {
        // Check variant.images (for level2 images)
        if (variant?.images) {
          Object.keys(variant.images).forEach(imageKey => {
            (variant.images[imageKey] || []).forEach((imageObj, imageIndex) => {
              if (!imageObj) return;
              const url = imageObj.previewUrl || imageObj.file?.name;
              if (url && !seenUrls.has(url)) {
                seenUrls.add(url);
                out.push({
                  image: imageObj,
                  level1Index,
                  variantIndex,
                  imageKey,
                  imageIndex,
                  uniqueId: `${level1Index}-${variantIndex}-${imageKey}-${imageIndex}-${url}`
                });
              }
            });
          });
        }
        // Check level3Fields images
        if (variant?.level3Fields && Array.isArray(variant.level3Fields)) {
          variant.level3Fields.forEach((level3Field, level3Index) => {
            if (level3Field?.images && Array.isArray(level3Field.images)) {
              level3Field.images.forEach((imageObj, imageIndex) => {
                if (!imageObj) return;
                const url = imageObj.previewUrl || imageObj.file?.name;
                if (url && !seenUrls.has(url)) {
                  seenUrls.add(url);
                  out.push({
                    image: imageObj,
                    level1Index,
                    variantIndex,
                    imageKey: 'level3',
                    level3Index,
                    imageIndex,
                    uniqueId: `${level1Index}-${variantIndex}-level3-${level3Index}-${imageIndex}-${url}`
                  });
                }
              });
            }
          });
        }
      });
    });
    return out;
  }, [variants]);

  // Get all images from all variants (for Delete Images - includes duplicates)
  const allImages = useMemo(() => {
    const out = [];
    (variants || []).forEach((variantArray, level1Index) => {
      (variantArray || []).forEach((variant, variantIndex) => {
        // Check variant.images (for level2 images)
        if (variant?.images) {
          Object.keys(variant.images).forEach(imageKey => {
            (variant.images[imageKey] || []).forEach((imageObj, imageIndex) => {
              if (!imageObj) return;
              out.push({
                image: imageObj,
                level1Index,
                variantIndex,
                imageKey,
                imageIndex,
                uniqueId: `${level1Index}-${variantIndex}-${imageKey}-${imageIndex}-${imageObj.previewUrl || imageObj.file?.name || Date.now()}-${Math.random()}`
              });
            });
          });
        }
        // Check level3Fields images
        if (variant?.level3Fields && Array.isArray(variant.level3Fields)) {
          variant.level3Fields.forEach((level3Field, level3Index) => {
            if (level3Field?.images && Array.isArray(level3Field.images)) {
              level3Field.images.forEach((imageObj, imageIndex) => {
                if (!imageObj) return;
                out.push({
                  image: imageObj,
                  level1Index,
                  variantIndex,
                  imageKey: 'level3',
                  level3Index,
                  imageIndex,
                  uniqueId: `${level1Index}-${variantIndex}-level3-${level3Index}-${imageIndex}-${imageObj.previewUrl || imageObj.file?.name || Date.now()}-${Math.random()}`
                });
              });
            }
          });
        }
      });
    });
    return out;
  }, [variants]);

  // Generic toggle selection helper
  const createToggleSelection = (selectedSet, setSelectedSet) => (uniqueId) => {
    const newSelected = new Set(selectedSet);
    if (newSelected.has(uniqueId)) {
      newSelected.delete(uniqueId);
    } else {
      newSelected.add(uniqueId);
    }
    setSelectedSet(newSelected);
  };

  // Generic select all helper
  const createSelectAll = (selectedSet, setSelectedSet, allItems) => () => {
    if (selectedSet.size === allItems.length) {
      setSelectedSet(new Set());
    } else {
      setSelectedSet(new Set(allItems.map(item => item.uniqueId)));
    }
  };

  const handleToggleImageSelection = useCallback(
    createToggleSelection(selectedImagesForBgRemoval, setSelectedImagesForBgRemoval),
    [selectedImagesForBgRemoval]
  );
  
  const handleSelectAll = useCallback(() => {
    const selectableForBgRemoval = uniqueImages.filter(
      img => img?.image && !img.image.backgroundRemoved
    );
    createSelectAll(selectedImagesForBgRemoval, setSelectedImagesForBgRemoval, selectableForBgRemoval)();
  }, [selectedImagesForBgRemoval, uniqueImages]);

  const handleToggleImageSelectionForDeletion = useCallback(
    createToggleSelection(selectedImagesForDeletion, setSelectedImagesForDeletion),
    [selectedImagesForDeletion]
  );
  
  const handleSelectAllForDeletion = useCallback(() => {
    createSelectAll(selectedImagesForDeletion, setSelectedImagesForDeletion, allImages)();
  }, [selectedImagesForDeletion, allImages]);

  // Remove Background Handler
  const handleRemoveBackgroundMultiple = async () => {
    if (selectedImagesForBgRemoval.size === 0) return;

    setProcessingBgRemoval(true);
    try {
      const selectedUniqueIds = Array.from(selectedImagesForBgRemoval);
      const selectedImages = uniqueImages.filter(img => img && selectedUniqueIds.includes(img.uniqueId));

      // Group images by URL to avoid duplicate processing
      const imagesByUrl = new Map();
      selectedImages.forEach(imageData => {
        if (!imageData || !imageData.image || !imageData.image.file) return;
        const imageUrl = imageData.image.previewUrl || imageData.image.file?.name;
        if (!imageUrl) return;
        
        if (!imagesByUrl.has(imageUrl)) {
          imagesByUrl.set(imageUrl, {
            image: imageData.image,
            imageDataList: []
          });
        }
        imagesByUrl.get(imageUrl).imageDataList.push(imageData);
      });

      // Process all unique images in parallel
      const processingPromises = Array.from(imagesByUrl.entries()).map(async ([imageUrl, { image, imageDataList }]) => {
        try {
          const formData = new FormData();
          formData.append('image', image.file);
          
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/single-gen/remove-background`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authCtx?.token}`
            },
            body: formData
          });

          if (!response.ok) {
            throw new Error('Background removal failed');
          }

          const result = await response.json();
          
          // Fetch the processed image
          const processedImageResponse = await fetch(result.image_url);
          const blob = await processedImageResponse.blob();
          const processedFile = new File([blob], image.file.name, { type: blob.type });
          const processedPreviewUrl = URL.createObjectURL(processedFile);

          return {
            imageUrl,
            processedImage: {
              ...image,
              file: processedFile,
              previewUrl: processedPreviewUrl,
              backgroundRemoved: true
            },
            imageDataList
          };
        } catch (error) {
          return {
            imageUrl,
            processedImage: null,
            imageDataList,
            error
          };
        }
      });

      // Wait for all processing to complete
      const results = await Promise.all(processingPromises);

      // Update variants with processed images (variants is array: variants[rowIndex] = level2 variants)
      const newVariants = (variants || []).map(row => [...(row || [])]);

      results.forEach(({ imageUrl, processedImage, imageDataList, error }) => {
        if (error || !processedImage) return; // Skip failed images

        // Update all instances of this image by level1Index
        imageDataList.forEach(({ level1Index, variantIndex, imageKey, imageIndex, level3Index }) => {
          const row = newVariants[level1Index];
          if (!row?.[variantIndex]) return;
          
          if (level3Index !== undefined && imageKey === 'level3') {
            // Handle level3Fields images
            const variant = row[variantIndex];
            const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [];
            if (level3Fields[level3Index]?.images?.[imageIndex]) {
              level3Fields[level3Index] = {
                ...level3Fields[level3Index],
                images: level3Fields[level3Index].images.map((img, j) =>
                  j === imageIndex ? processedImage : img
                )
              };
              newVariants[level1Index] = row.map((v, i) =>
                i !== variantIndex ? v : { ...v, level3Fields }
              );
            }
          } else {
            // Handle variant.images (level2 images)
            if (row[variantIndex].images?.[imageKey]?.[imageIndex]) {
              newVariants[level1Index] = row.map((v, i) =>
                i !== variantIndex ? v : {
                  ...v,
                  images: {
                    ...(v.images || {}),
                    [imageKey]: (v.images?.[imageKey] || []).map((img, j) =>
                      j === imageIndex ? processedImage : img
                    )
                  }
                }
              );
            }
          }
        });

        // Update all other instances with matching URL (by iterating array by index)
        (newVariants || []).forEach((variantArray, l1Idx) => {
          (variantArray || []).forEach((variant, vIdx) => {
            // Update variant.images (level2 images)
            if (variant?.images) {
              Object.keys(variant.images).forEach(key => {
                const imageArray = variant.images[key] || [];
                imageArray.forEach((img, imgIdx) => {
                  const imgUrl = img?.previewUrl || img?.file?.name;
                  if (imgUrl === imageUrl && img !== processedImage) {
                    const row = newVariants[l1Idx] || [];
                    newVariants[l1Idx] = row.map((v, i) =>
                      i !== vIdx ? v : {
                        ...v,
                        images: {
                          ...(v.images || {}),
                          [key]: (v.images?.[key] || []).map((im, imgI) =>
                            imgI === imgIdx ? { ...processedImage } : im
                          )
                        }
                      }
                    );
                  }
                });
              });
            }
            // Update level3Fields images
            if (variant?.level3Fields && Array.isArray(variant.level3Fields)) {
              variant.level3Fields.forEach((level3Field, l3Idx) => {
                if (level3Field?.images && Array.isArray(level3Field.images)) {
                  level3Field.images.forEach((img, imgIdx) => {
                    const imgUrl = img?.previewUrl || img?.file?.name;
                    if (imgUrl === imageUrl && img !== processedImage) {
                      const row = newVariants[l1Idx] || [];
                      const level3Fields = Array.isArray(row[vIdx]?.level3Fields) ? [...row[vIdx].level3Fields] : [];
                      level3Fields[l3Idx] = {
                        ...level3Fields[l3Idx],
                        images: level3Fields[l3Idx].images.map((im, imgI) =>
                          imgI === imgIdx ? { ...processedImage } : im
                        )
                      };
                      newVariants[l1Idx] = row.map((v, i) =>
                        i !== vIdx ? v : { ...v, level3Fields }
                      );
                    }
                  });
                }
              });
            }
          });
        });
      });

      setVariants(newVariants);
      setRemoveBgModal(false);
      setSelectedImagesForBgRemoval(new Set());
    } catch (error) {
      alert('Failed to remove backgrounds. Please try again.');
    } finally {
      setProcessingBgRemoval(false);
    }
  };

  // Delete Images Handlers
  const handleDeleteSelectedImages = () => {
    if (selectedImagesForDeletion.size === 0) return;
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteImages = () => {
    if (selectedImagesForDeletion.size === 0) return;

    const selectedUniqueIds = Array.from(selectedImagesForDeletion);
    const selectedImages = allImages.filter(img => img && selectedUniqueIds.includes(img.uniqueId));

    // variants is array: variants[rowIndex] = level2 variants
    const newVariants = (variants || []).map(row => [...(row || [])]);

    // Store deleted images with metadata for restoration (use level1Index)
    const timestamp = Date.now();
    const imagesToStore = selectedImages.map(({ image, level1Index, variantIndex, imageKey, imageIndex, level3Index }, idx) => ({
      image,
      level1Index,
      variantIndex,
      imageKey,
      imageIndex,
      level3Index,
      deletedAt: timestamp,
      uniqueId: `deleted-${level1Index}-${variantIndex}-${imageKey}-${imageIndex}-${level3Index !== undefined ? `l3-${level3Index}-` : ''}${timestamp}-${idx}-${Math.random().toString(36).substr(2, 9)}`
    }));

    // Add to deleted images array
    setDeletedImages(prev => {
      const updated = [...prev, ...imagesToStore];
      deletedImagesRef.current = updated;
      return updated;
    });

    // Group deletions by location and sort indices in descending order
    const deletionsByLocation = {};
    selectedImages.forEach(({ level1Index, variantIndex, imageKey, imageIndex, level3Index }) => {
      const key = `${level1Index}-${variantIndex}-${imageKey}${level3Index !== undefined ? `-l3-${level3Index}` : ''}`;
      if (!deletionsByLocation[key]) {
        deletionsByLocation[key] = [];
      }
      deletionsByLocation[key].push({ level1Index, variantIndex, imageKey, imageIndex, level3Index });
    });

    // Delete images (descending order to avoid index shifting). Use array indexing.
    Object.values(deletionsByLocation).forEach(locations => {
      locations.sort((a, b) => b.imageIndex - a.imageIndex).forEach(({ level1Index, variantIndex, imageKey, imageIndex, level3Index }) => {
        const row = newVariants[level1Index];
        if (!row?.[variantIndex]) return;
        
        if (level3Index !== undefined && imageKey === 'level3') {
          // Handle level3Fields images
          const variant = row[variantIndex];
          const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [];
          if (level3Fields[level3Index]?.images && imageIndex >= 0 && imageIndex < level3Fields[level3Index].images.length) {
            level3Fields[level3Index] = {
              ...level3Fields[level3Index],
              images: level3Fields[level3Index].images.filter((_, j) => j !== imageIndex)
            };
            newVariants[level1Index] = row.map((v, i) =>
              i !== variantIndex ? v : { ...v, level3Fields }
            );
          }
        } else {
          // Handle variant.images (level2 images)
          if (row[variantIndex].images?.[imageKey] && imageIndex >= 0 && imageIndex < row[variantIndex].images[imageKey].length) {
            newVariants[level1Index] = row.map((v, i) =>
              i !== variantIndex ? v : {
                ...v,
                images: {
                  ...(v.images || {}),
                  [imageKey]: (v.images?.[imageKey] || []).filter((_, j) => j !== imageIndex)
                }
              }
            );
          }
        }
      });
    });

    setVariants(newVariants);
    setDeleteImagesModal(false);
    setSelectedImagesForDeletion(new Set());
    setShowDeleteConfirmation(false);
  };

  const cancelDeleteImages = () => {
    setShowDeleteConfirmation(false);
  };

  // Restore Images Handlers
  const handleToggleImageSelectionForRestore = useCallback(
    createToggleSelection(selectedImagesForRestore, setSelectedImagesForRestore),
    [selectedImagesForRestore]
  );
  
  const handleSelectAllForRestore = useCallback(() => {
    createSelectAll(selectedImagesForRestore, setSelectedImagesForRestore, deletedImages)();
  }, [selectedImagesForRestore, deletedImages]);

  const handleRestoreSelectedImages = () => {
    if (selectedImagesForRestore.size === 0) return;

    const selectedUniqueIds = Array.from(selectedImagesForRestore);
    const imagesToRestore = deletedImages.filter(img => img && img.uniqueId && selectedUniqueIds.includes(img.uniqueId));

    if (imagesToRestore.length === 0) {
      return;
    }

    // variants is array: variants[rowIndex] = level2 variants
    const newVariants = (variants || []).map(row => (row || []).map(v => ({ ...v, images: { ...(v.images || {}) } })));

    // Group restorations by location (use level1Index)
    const restorationsByLocation = {};
    imagesToRestore.forEach(({ image, level1Index, variantIndex, imageKey, imageIndex, level3Index, uniqueId }) => {
      if (!image) {
        return;
      }
      const key = `${level1Index}-${variantIndex}-${imageKey}${level3Index !== undefined ? `-l3-${level3Index}` : ''}`;
      if (!restorationsByLocation[key]) {
        restorationsByLocation[key] = [];
      }
      restorationsByLocation[key].push({ image, level1Index, variantIndex, imageKey, imageIndex, level3Index, uniqueId });
    });

    // Track which images were successfully restored
    const successfullyRestoredIds = new Set();

    // Restore images (sort by index descending to insert from end to beginning)
    Object.values(restorationsByLocation).forEach(locations => {
      const sorted = [...locations].sort((a, b) => b.imageIndex - a.imageIndex);
      sorted.forEach(({ image, level1Index, variantIndex, imageKey, imageIndex, level3Index, uniqueId }) => {
        while (newVariants.length <= level1Index) {
          newVariants.push([]);
        }
        let row = newVariants[level1Index] || [];
        while (row.length <= variantIndex) {
          row = [...row, { level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} }];
        }
        newVariants[level1Index] = row;
        let variant = newVariants[level1Index][variantIndex] || { level2: '', level3Fields: [{ value: '', sku: '', images: [] }], sku: '', images: {} };
        
        if (level3Index !== undefined && imageKey === 'level3') {
          // Handle level3Fields images
          const level3Fields = Array.isArray(variant.level3Fields) ? [...variant.level3Fields] : [{ value: '', sku: '', images: [] }];
          while (level3Fields.length <= level3Index) {
            level3Fields.push({ value: '', sku: '', images: [] });
          }
          const level3Field = level3Fields[level3Index] || { value: '', sku: '', images: [] };
          const arr = Array.isArray(level3Field.images) ? level3Field.images : [];
          const insertIndex = Math.min(imageIndex, arr.length);
          const nextArr = [...arr.slice(0, insertIndex), image, ...arr.slice(insertIndex)];
          level3Fields[level3Index] = { ...level3Field, images: nextArr };
          newVariants[level1Index] = (newVariants[level1Index] || []).map((v, i) =>
            i !== variantIndex ? v : { ...v, level3Fields }
          );
        } else {
          // Handle variant.images (level2 images)
          if (!variant.images) variant = { ...variant, images: {} };
          const arr = variant.images[imageKey] || [];
          const insertIndex = Math.min(imageIndex, arr.length);
          const nextArr = [...arr.slice(0, insertIndex), image, ...arr.slice(insertIndex)];
          newVariants[level1Index] = (newVariants[level1Index] || []).map((v, i) =>
            i !== variantIndex ? v : { ...v, images: { ...(v.images || {}), [imageKey]: nextArr } }
          );
        }
        successfullyRestoredIds.add(uniqueId);
      });
    });

    setVariants(newVariants);

    // Remove only successfully restored images from deleted images array
    // Note: We don't revoke blob URLs here because the images are being restored
    // and need their preview URLs. The blob URLs will be cleaned up on unmount.
    setDeletedImages(prev => {
      const remaining = prev.filter(img => !successfullyRestoredIds.has(img.uniqueId));
      deletedImagesRef.current = remaining;
      return remaining;
    });

    // Clear selection and close modal
    setSelectedImagesForRestore(new Set());
    setRestoreImagesModal(false);
  };

  // Update refs when values change
  useEffect(() => {
    variantsRef.current = variants;
  }, [variants]);

  // Cleanup blob URLs on unmount only
  useEffect(() => {
    return () => {
      // Cleanup all blob URLs from variants when component unmounts
      Object.values(variantsRef.current).forEach(variantArray => {
        variantArray.forEach(variant => {
          if (variant.images) {
            Object.values(variant.images).forEach(imageArray => {
              if (Array.isArray(imageArray)) {
                imageArray.forEach(obj => {
                  const previewUrl = obj?.previewUrl;
                  if (previewUrl && previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(previewUrl);
                  }
                });
              }
            });
          }
        });
      });
      
      // Cleanup all blob URLs from deleted images when component unmounts
      deletedImagesRef.current.forEach(deletedImg => {
        const previewUrl = deletedImg?.image?.previewUrl;
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    };
  }, []); // Empty dependency array - only runs on mount/unmount


  // Persist variants tab data up to parent for drafts + submission
  useEffect(() => {
    if (typeof setVariantsDraftData === 'function') {
      setVariantsDraftData({
        variantType,
        level1Values,
        variants
      });
    }
  }, [variantType, level1Values, variants, setVariantsDraftData]);

  // Notify parent of validation state changes
  useEffect(() => {
    if (typeof onValidationChange === 'function') {
      const isValid = validateVariants();
      onValidationChange(isValid);
    }
  }, [validateVariants, onValidationChange]);

  return (
    <div className={styles.variantsContainer}>
      {/* Step 2 Header: attributes + bulk upload + guide + search + actions */}
      <div className={styles.step2Header}>
        {/* Back to attributes button - own row */}
        {typeof onBackToStep1 === 'function' && (
          <div className={styles.step2BackRow}>
            <button
              type="button"
              className={styles.backToStep1Button}
              onClick={onBackToStep1}
              disabled={isLoading}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Back to attributes</span>
            </button>
          </div>
        )}

        {/* Row 1: Attribute tags + Bulk Image Upload */}
        <div className={styles.step2HeaderRow1}>
          <div className={styles.step2AttributesRow}>
            {[level1Type, level2Type, level3Type].filter(Boolean).map((label, idx) => (
              <div key={idx} className={styles.step2AttributeTag}>
                <span className={styles.step2AttributeLabel}>Attribute {idx + 1}</span>
                <span className={styles.step2AttributeValue}>{label}</span>
              </div>
            ))}
          </div>
          <input
            ref={bulkZipInputRef}
            type="file"
            accept=".zip"
            onChange={handleBulkZipUpload}
            className={styles.bulkZipInput}
            aria-hidden="true"
            tabIndex={-1}
          />
          <button
            type="button"
            className={styles.bulkImageUploadButton}
            disabled={isLoading}
            aria-label="Bulk image upload (ZIP)"
            onClick={() => bulkZipInputRef.current?.click()}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 0.299805C8.71786 0.299805 9.29959 0.881823 9.2998 1.59961V6.7002H14.4004C15.1182 6.70041 15.7002 7.28213 15.7002 8C15.7002 8.71786 15.1182 9.29959 14.4004 9.2998H9.2998V14.4004C9.29959 15.1182 8.71786 15.7002 8 15.7002C7.28214 15.7002 6.70041 15.1182 6.7002 14.4004V9.2998H1.59961C0.881823 9.29959 0.299805 8.71786 0.299805 8C0.299805 7.28213 0.881821 6.70041 1.59961 6.7002H6.7002V1.59961C6.70041 0.881822 7.28214 0.299805 8 0.299805Z" fill="url(#paint0_linear_189_538)" stroke="#F5FAFF" strokeWidth="0.6"/>
            <defs>
            <linearGradient id="paint0_linear_189_538" x1="-5.96046e-08" y1="8" x2="14.6857" y2="16" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2766AA"/>
            <stop offset="1" stopColor="#0752B0"/>
            </linearGradient>
            </defs>
            </svg>
            <span>Bulk Image Upload</span>
          </button>
        </div>

        {/* Bulk Upload Guide */}
        <div className={styles.bulkUploadGuide}>
          <div className={styles.bulkUploadGuideIcon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12V3M9 3L5 7M9 3L13 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 15H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className={styles.bulkUploadGuideContent}>
            <div className={styles.bulkUploadGuideTitle}>Bulk Upload Guide:</div>
            <div className={styles.bulkUploadGuideText}>
              To auto-assign images, upload a ZIP where <strong>folder names match the SKU-IDs</strong> below. Internal images will be assigned to their respective variant cards. Upload only up to 5 images for each SKU.
            </div>
          </div>
        </div>

        <div className={styles.bulkUploadGuideSeparator}></div>

        {/* Remove Background + Restore Deleted */}
        <div className={styles.step2SearchRow}>
          <div className={styles.step2Actions}>
            <button
              type="button"
              className={styles.step2RemoveBgBtn}
              onClick={() => { setRemoveBgModal(true); setSelectedImagesForBgRemoval(new Set()); }}
              disabled={isLoading || uniqueImages.length === 0}
            >
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.7793 0.701425C14.4289 0.366559 13.9629 0.179688 13.4782 0.179688C12.9935 0.179687 12.5274 0.366559 12.177 0.701425L10.874 2.00258L5.47555 7.40104C5.2405 7.63723 5.0735 7.93245 4.99214 8.25559L4.45537 10.4027C4.40974 10.5832 4.40581 10.7718 4.44386 10.954C4.48191 11.1363 4.56094 11.3075 4.67497 11.4547C4.789 11.6019 4.93504 11.7212 5.10202 11.8036C5.269 11.886 5.45254 11.9293 5.63873 11.9302C5.74049 11.9301 5.84186 11.9176 5.94056 11.8928L8.08767 11.356C8.41117 11.2749 8.70655 11.1074 8.94222 10.8714L14.3407 5.47293L15.6424 4.17178C15.9875 3.82665 16.1813 3.35863 16.1813 2.87063C16.1813 2.38262 15.9875 1.9146 15.6424 1.56947L14.7793 0.701425ZM8.07786 10.004C7.99933 10.0825 7.90095 10.1383 7.79321 10.1653L5.64609 10.7027L6.18349 8.55557C6.21023 8.44768 6.26578 8.34908 6.34421 8.27031L11.3096 3.30557L13.0451 5.04044L8.07786 10.004ZM14.7793 3.30373L13.9106 4.17116L12.1752 2.43629L13.0432 1.56886C13.16 1.45719 13.3153 1.39487 13.4769 1.39487C13.6385 1.39487 13.7939 1.45719 13.9106 1.56886L14.7793 2.43629C14.8943 2.55134 14.9589 2.70734 14.9589 2.87001C14.9589 3.03268 14.8943 3.18869 14.7793 3.30373Z" fill="#2766AA" stroke="#2766AA" strokeWidth="0.357951" />
                <path d="M3.24699 16.0883H11.8355C12.6487 16.0873 13.4283 15.7639 14.0033 15.1888C14.5783 14.6138 14.9018 13.8342 14.9028 13.021V7.2606C14.9028 7.0979 14.8381 6.94186 14.7231 6.82681C14.608 6.71177 14.452 6.64714 14.2893 6.64714C14.1266 6.64714 13.9706 6.71177 13.8555 6.82681C13.7405 6.94186 13.6758 7.0979 13.6758 7.2606V13.021C13.6758 13.5091 13.4819 13.9772 13.1368 14.3223C12.7917 14.6675 12.3236 14.8614 11.8355 14.8614H3.24699C2.75889 14.8614 2.29078 14.6675 1.94565 14.3223C1.60051 13.9772 1.40661 13.5091 1.40661 13.021V4.43254C1.40661 3.94444 1.60051 3.47633 1.94565 3.13119C2.29078 2.78605 2.75889 2.59216 3.24699 2.59216H9.16076C9.32346 2.59216 9.4795 2.52752 9.59454 2.41248C9.70959 2.29743 9.77422 2.1414 9.77422 1.9787C9.77422 1.816 9.70959 1.65996 9.59454 1.54491C9.4795 1.42987 9.32346 1.36523 9.16076 1.36523H3.24699C2.43379 1.36621 1.65418 1.68968 1.07916 2.2647C0.504136 2.83972 0.180662 3.61934 0.179688 4.43254V13.021C0.180662 13.8342 0.504136 14.6138 1.07916 15.1888C1.65418 15.7639 2.43379 16.0873 3.24699 16.0883Z" fill="#2766AA" stroke="#2766AA" strokeWidth="0.357951" />
              </svg>
              <span>Remove Background</span>
            </button>
            <button
              type="button"
              className={styles.step2DeleteImagesBtn}
              onClick={() => { setDeleteImagesModal(true); setSelectedImagesForDeletion(new Set()); }}
              disabled={isLoading || allImages.length === 0}
            >
              <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.8329 3.0648H10.9834L10.6163 1.59597C10.3815 0.655767 9.54037 0 8.5722 0H4.83589C3.86769 0 3.02733 0.655767 2.79177 1.59597L2.42474 3.0648H0.574605C0.257129 3.0648 0 3.32193 0 3.63941C0 3.95688 0.257129 4.21401 0.574605 4.21401H0.805881L1.43147 12.9831C1.50976 14.0799 2.43272 14.9397 3.53315 14.9397H9.87311C10.9735 14.9397 11.8964 14.08 11.9748 12.9831L12.6004 4.21401H12.8316C13.1491 4.21401 13.4063 3.95688 13.4063 3.63941C13.4063 3.32193 13.1491 3.0648 12.8316 3.0648H12.8329ZM3.90716 1.87465C4.01418 1.44729 4.3963 1.14921 4.83659 1.14921H8.5729C9.01318 1.14921 9.39531 1.448 9.50232 1.87465L9.79968 3.0648H3.60903L3.90716 1.87465ZM10.8296 12.9014C10.7944 13.3998 10.3743 13.7905 9.87436 13.7905H3.53366C3.03303 13.7905 2.61358 13.3998 2.57838 12.9014L1.95781 4.21407H11.4495L10.8296 12.9014ZM7.66149 10.5347V6.70427C7.66149 6.38679 7.91862 6.12966 8.2361 6.12966C8.55357 6.12966 8.8107 6.38679 8.8107 6.70427V10.5347C8.8107 10.8522 8.55357 11.1093 8.2361 11.1093C7.91862 11.1093 7.66149 10.8522 7.66149 10.5347ZM4.59743 10.5347V6.70427C4.59743 6.38679 4.85455 6.12966 5.17203 6.12966C5.48951 6.12966 5.74663 6.38679 5.74663 6.70427V10.5347C5.74663 10.8522 5.48951 11.1093 5.17203 11.1093C4.85455 11.1093 4.59743 10.8522 4.59743 10.5347Z" fill="#DE524A"/>
              </svg>
              <span>Delete Images</span>
            </button>
            {deletedImages.length > 0 && (
              <button
                type="button"
                className={styles.step2RestoreDeletedBtn}
                onClick={() => { setRestoreImagesModal(true); setSelectedImagesForRestore(new Set()); }}
                disabled={isLoading}
              >
                <span>Restore Deleted</span>
              </button>
            )}
          </div>
        </div>

        {/* Table header row: Color, Size, Pack, SKU, Images */}
        <div className={styles.step2TableHeader}>
          {[level1Type, level2Type, level3Type].filter(Boolean).map((label, idx) => (
            <div key={idx} className={`${styles.step2TableHeaderCell} ${styles.step2TableHeaderLevelCell}`}>{label}</div>
          ))}
          <div className={styles.step2TableHeaderCell}>SKU</div>
          <div className={styles.step2TableHeaderCell}>Images</div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {showLevel1() ? (
          level1Values.map((level1Val, level1Index) => {
            const level2Variants = variants[level1Index] || [];
            const variantCount = level2Variants.length;
            const isSingleVariant = false;
            
            return (
              <div key={level1Index} className={styles.level1GroupWrapper} style={variantCount === 2 ? { alignItems: 'center' } : {}}>
                {isSingleVariant ? (
                  /* Single Row Layout - columns with field + Add button directly below */
                  <div className={`${styles.level2VariantsContainer} ${styles.level2VariantsContainerSingleRow}`}>
                    <div className={`${styles.flowchartRow} ${styles.step2TableRow}`}>
                      {/* Level 1 column: input + Add Level 1 directly below */}
                      <div className={`${styles.step2TableCell} ${styles.fieldColumnWithAdd}`}>
                        <div className={styles.level1Variant}>
                          <input
                            type="text"
                            value={level1Val}
                            onChange={(e) => updateLevel1(level1Index, e.target.value)}
                            className={styles.level1Input}
                            placeholder={level1Type || "Variant Level 1"}
                            disabled={isLoading}
                          />
                          {level1Values.length > 1 && (
                            <button
                              type="button"
                              className={styles.deleteButton}
                              onClick={() => removeLevel1(level1Index)}
                              disabled={isLoading}
                            >
                              <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.5169 3.46697H12.4246L12.0094 1.80539C11.7437 0.741818 10.7923 0 9.69707 0H5.47047C4.37522 0 3.42458 0.741818 3.15811 1.80539L2.74292 3.46697H0.650006C0.29087 3.46697 0 3.75784 0 4.11698C0 4.47612 0.29087 4.76699 0.650006 4.76699H0.911631L1.61932 14.6868C1.70788 15.9275 2.75195 16.9002 3.99678 16.9002H11.1687C12.4134 16.9002 13.4575 15.9276 13.5461 14.6868L14.2538 4.76699H14.5155C14.8746 4.76699 15.1655 4.47612 15.1655 4.11698C15.1655 3.75784 14.8746 3.46697 14.5155 3.46697H14.5169ZM4.41987 2.12064C4.54094 1.6372 4.9732 1.30001 5.47126 1.30001H9.69786C10.1959 1.30001 10.6282 1.63801 10.7492 2.12064L11.0856 3.46697H4.08262L4.419 2.12064H4.41987ZM12.2507 14.5943C12.2109 15.1582 11.7356 15.6002 11.1701 15.6002H3.99736C3.43103 15.6002 2.95654 15.1582 2.91673 14.5943L2.21472 4.76705H12.952L12.25 14.5943H12.2507ZM8.66686 11.9171V7.58402C8.66686 7.22488 8.95773 6.93401 9.31686 6.93401C9.676 6.93401 9.96687 7.22488 9.96687 7.58402V11.9171C9.96687 12.2762 9.676 12.5671 9.31686 12.5671C8.95773 12.5671 8.66686 12.2762 8.66686 11.9171ZM5.20071 11.9171V7.58402C5.20071 7.22488 5.49158 6.93401 5.85072 6.93401C6.20985 6.93401 6.50072 7.22488 6.50072 7.58402V11.9171C6.50072 12.2762 6.20985 12.5671 5.85072 12.5671C5.49158 12.5671 5.20071 12.2762 5.20071 11.9171Z" fill="#737373"/>
                              </svg>
                            </button>
                          )}
                        </div>
                        {showLevel1() && (
                          <div className={styles.addButtonBelowField}>
                            <button
                              type="button"
                              className={styles.addLevel1Button}
                              onClick={addLevel1Variant}
                              disabled={isLoading}
                            >
                              <AddButtonIcon />
                              Add {level1Type || "Variant Level 1"}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Level 2 column: inputs + Add Level 2 directly below */}
                      {showLevel2() && (
                        <div className={`${styles.step2TableCell} ${styles.fieldColumnWithAdd}`}>
                          {level2Variants.map((variant, level2Index) => (
                            <div key={level2Index} className={styles.level2FieldWrapper}>
                              <input
                                type="text"
                                value={variant.level2 || ''}
                                onChange={(e) => updateLevel2(level1Index, level2Index, e.target.value)}
                                className={styles.level2Input}
                                placeholder={level2Type || "Variant Level 2"}
                                disabled={isLoading}
                              />
                            </div>
                          ))}
                          <div className={styles.addButtonBelowField}>
                            <button
                              type="button"
                              className={styles.addLevel2VariantButton}
                              onClick={() => addLevel2Variant(level1Index)}
                              disabled={isLoading || level2Variants.length >= 5}
                            >
                              <AddButtonIcon />
                              Add {level2Type || "Variant Level 2"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Level 3 / SKU / Images columns - one per variant; SKU and Images as separate cells to align with header */}
                      {level2Variants.map((variant, level2Index) => (
                        showLevel3() ? (
                          <div key={level2Index} className={styles.level2VariantFlowchart}>
                            <div className={styles.level3Container}>
                              {renderLevel3Fields(level1Index, variant, level2Index, level3Type)}
                            </div>
                          </div>
                        ) : (
                          <React.Fragment key={level2Index}>
                            <div className={styles.step2TableCell}>
                              <input
                                type="text"
                                value={variant.sku || ''}
                                onChange={(e) => updateSku(level1Index, level2Index, e.target.value)}
                                className={styles.skuInput}
                                placeholder="SKU ID"
                                disabled={isLoading}
                              />
                            </div>
                            <div className={styles.step2TableCell}>
                              {renderImageUpload(variant, level1Index, level2Index, undefined, `image-upload-l1-${level1Index}-l2-${level2Index}`)}
                            </div>
                          </React.Fragment>
                        )
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Multi-Row Layout - Level 1 on left, Level 2/3 branching on right */
                  <>
                    {/* Left Column - Level 1 Field + Add Level 1 directly below */}
                    <div className={styles.level1FieldContainer} style={variantCount === 2 ? { alignSelf: 'center' } : {}}>
                      <div className={styles.fieldColumnWithAdd}>
                        <div className={styles.level1Variant}>
                          {renderEnumOrTextInput(
                            level1Val,
                            (v) => updateLevel1(level1Index, v),
                            styles.level1Input,
                            level1Type,
                            level1Type || 'Variant Level 1'
                          )}
                          {level1Values.length > 1 && (
                            <button
                              type="button"
                              className={styles.deleteButton}
                              onClick={() => removeLevel1(level1Index)}
                              disabled={isLoading}
                            >
                              <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.5169 3.46697H12.4246L12.0094 1.80539C11.7437 0.741818 10.7923 0 9.69707 0H5.47047C4.37522 0 3.42458 0.741818 3.15811 1.80539L2.74292 3.46697H0.650006C0.29087 3.46697 0 3.75784 0 4.11698C0 4.47612 0.29087 4.76699 0.650006 4.76699H0.911631L1.61932 14.6868C1.70788 15.9275 2.75195 16.9002 3.99678 16.9002H11.1687C12.4134 16.9002 13.4575 15.9276 13.5461 14.6868L14.2538 4.76699H14.5155C14.8746 4.76699 15.1655 4.47612 15.1655 4.11698C15.1655 3.75784 14.8746 3.46697 14.5155 3.46697H14.5169ZM4.41987 2.12064C4.54094 1.6372 4.9732 1.30001 5.47126 1.30001H9.69786C10.1959 1.30001 10.6282 1.63801 10.7492 2.12064L11.0856 3.46697H4.08262L4.419 2.12064H4.41987ZM12.2507 14.5943C12.2109 15.1582 11.7356 15.6002 11.1701 15.6002H3.99736C3.43103 15.6002 2.95654 15.1582 2.91673 14.5943L2.21472 4.76705H12.952L12.25 14.5943H12.2507ZM8.66686 11.9171V7.58402C8.66686 7.22488 8.95773 6.93401 9.31686 6.93401C9.676 6.93401 9.96687 7.22488 9.96687 7.58402V11.9171C9.96687 12.2762 9.676 12.5671 9.31686 12.5671C8.95773 12.5671 8.66686 12.2762 8.66686 11.9171ZM5.20071 11.9171V7.58402C5.20071 7.22488 5.49158 6.93401 5.85072 6.93401C6.20985 6.93401 6.50072 7.22488 6.50072 7.58402V11.9171C6.50072 12.2762 6.20985 12.5671 5.85072 12.5671C5.49158 12.5671 5.20071 12.2762 5.20071 11.9171Z" fill="#737373"/>
                              </svg>
                            </button>
                          )}
                        </div>
                        {showLevel1() && (
                          <div className={styles.addButtonBelowField}>
                            <button
                              type="button"
                              className={styles.addLevel1Button}
                              onClick={addLevel1Variant}
                              disabled={isLoading}
                            >
                              <AddButtonIcon />
                              Add {level1Type || "Variant Level 1"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Level 2 and Level 3 Variants */}
                    <div className={styles.level1Level2Group}>
                      <div className={styles.level2VariantsContainer}>
                        {level2Variants.map((variant, level2Index) => (
                          <div key={level2Index} className={styles.level2VariantFlowchart}>
                            <div className={`${styles.flowchartRow} ${styles.step2TableRow}`}>
                              {showLevel2() && (
                                <div className={`${styles.step2TableCell} ${styles.level2FieldWrapper}`}>
                                  {renderEnumOrTextInput(
                                    variant.level2 || '',
                                    (v) => updateLevel2(level1Index, level2Index, v),
                                    styles.level2Input,
                                    level2Type,
                                    level2Type || 'Variant Level 2'
                                  )}
                                </div>
                              )}
                              
                              {showLevel3() && (
                                <>
                                  <div className={styles.level3Container}>
                                    {renderLevel3Fields(level1Index, variant, level2Index, level3Type)}
                                  </div>
                                </>
                              )}
                              
                              {!showLevel3() && (
                                <>
                                  <div className={styles.step2TableCell}>
                                    <input
                                      type="text"
                                      value={variant.sku || ''}
                                      onChange={(e) => updateSku(level1Index, level2Index, e.target.value)}
                                      className={styles.skuInput}
                                      placeholder="SKU ID"
                                      disabled={isLoading}
                                    />
                                  </div>
                                  <div className={styles.step2TableCell}>
                                    {renderImageUpload(variant, level1Index, level2Index, undefined, `image-upload-l1-${level1Index}-l2-${level2Index}`)}
                                  </div>
                                </>
                              )}
                              
                              {showLevel2() && !showLevel3() && level2Variants.length > 1 && (
                                <button
                                  type="button"
                                  className={styles.deleteButton}
                                  onClick={() => removeLevel2Variant(level1Index, level2Index)}
                                  disabled={isLoading}
                                >
                                  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.5169 3.46697H12.4246L12.0094 1.80539C11.7437 0.741818 10.7923 0 9.69707 0H5.47047C4.37522 0 3.42458 0.741818 3.15811 1.80539L2.74292 3.46697H0.650006C0.29087 3.46697 0 3.75784 0 4.11698C0 4.47612 0.29087 4.76699 0.650006 4.76699H0.911631L1.61932 14.6868C1.70788 15.9275 2.75195 16.9002 3.99678 16.9002H11.1687C12.4134 16.9002 13.4575 15.9276 13.5461 14.6868L14.2538 4.76699H14.5155C14.8746 4.76699 15.1655 4.47612 15.1655 4.11698C15.1655 3.75784 14.8746 3.46697 14.5155 3.46697H14.5169ZM4.41987 2.12064C4.54094 1.6372 4.9732 1.30001 5.47126 1.30001H9.69786C10.1959 1.30001 10.6282 1.63801 10.7492 2.12064L11.0856 3.46697H4.08262L4.419 2.12064H4.41987ZM12.2507 14.5943C12.2109 15.1582 11.7356 15.6002 11.1701 15.6002H3.99736C3.43103 15.6002 2.95654 15.1582 2.91673 14.5943L2.21472 4.76705H12.952L12.25 14.5943H12.2507ZM8.66686 11.9171V7.58402C8.66686 7.22488 8.95773 6.93401 9.31686 6.93401C9.676 6.93401 9.96687 7.22488 9.96687 7.58402V11.9171C9.96687 12.2762 9.676 12.5671 9.31686 12.5671C8.95773 12.5671 8.66686 12.2762 8.66686 11.9171ZM5.20071 11.9171V7.58402C5.20071 7.22488 5.49158 6.93401 5.85072 6.93401C6.20985 6.93401 6.50072 7.22488 6.50072 7.58402V11.9171C6.50072 12.2762 6.20985 12.5671 5.85072 12.5671C5.49158 12.5671 5.20071 12.2762 5.20071 11.9171Z" fill="#737373"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Add Level 2 directly below Level 2 variants */}
                      {showLevel2() && (
                        <div className={styles.addButtonBelowField}>
                          <button
                            type="button"
                            className={styles.addLevel2VariantButton}
                            onClick={() => addLevel2Variant(level1Index)}
                            disabled={isLoading || level2Variants.length >= 5}
                          >
                            <AddButtonIcon />
                            Add {level2Type || "Variant Level 2"}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        ) : (
          // When Level 1 is not shown, use row index 0
          <div className={styles.level2VariantsContainer}>
            {(variants[0] || []).map((variant, level2Index) => (
              <div key={level2Index} className={styles.level2VariantFlowchart}>
                <div className={styles.flowchartRow}>
                  {showLevel2() && (
                    <div className={styles.level2FieldWrapper}>
                      <input
                        type="text"
                        value={variant.level2 || ''}
                        onChange={(e) => updateLevel2(0, level2Index, e.target.value)}
                        className={styles.level2Input}
                        placeholder={level2Type || "Variant Level 2"}
                        disabled={isLoading}
                      />
                    </div>
                  )}
                  
                  {showLevel3() && (
                    <>
                      <div className={styles.level3Container}>
                        {renderLevel3Fields(0, variant, level2Index, level3Type)}
                      </div>
                    </>
                  )}
                  
                  {!showLevel3() && (
                    <>
                      <input
                        type="text"
                        value={variant.sku || ''}
                        onChange={(e) => updateSku(0, level2Index, e.target.value)}
                        className={styles.skuInput}
                        placeholder="SKU ID"
                        disabled={isLoading}
                      />
                      {renderImageUpload(variant, 0, level2Index, undefined, `image-upload-no-level1-level2`)}
                    </>
                  )}
                  
                  {showLevel2() && (variants[0] || []).length > 1 && (
                    <button
                      type="button"
                      className={styles.removeLevel2Button}
                      onClick={() => removeLevel2Variant(0, level2Index)}
                      disabled={isLoading}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Add Level 2 directly below when Level 1 is not shown */}
            {showLevel2() && (
              <div className={styles.addButtonBelowField}>
                <button
                  type="button"
                  className={styles.addLevel2VariantButton}
                  onClick={() => addLevel2Variant(0)}
                  disabled={isLoading || (variants[0] || []).length >= 5}
                >
                  <AddButtonIcon />
                  Add {level2Type || "Variant Level 2"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <RemoveBackgroundModal
        isOpen={removeBgModal}
        onClose={() => setRemoveBgModal(false)}
        uniqueImages={uniqueImages}
        selectedImages={selectedImagesForBgRemoval}
        onToggleSelection={handleToggleImageSelection}
        onSelectAll={handleSelectAll}
        onRemoveBackground={handleRemoveBackgroundMultiple}
        processingBgRemoval={processingBgRemoval}
      />

      <DeleteImagesModal
        isOpen={deleteImagesModal}
        onClose={() => setDeleteImagesModal(false)}
        allImages={allImages}
        selectedImages={selectedImagesForDeletion}
        onToggleSelection={handleToggleImageSelectionForDeletion}
        onSelectAll={handleSelectAllForDeletion}
        onDeleteSelected={handleDeleteSelectedImages}
        isVariants={true}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={cancelDeleteImages}
        onConfirm={confirmDeleteImages}
      />

      <RestoreImagesModal
        isOpen={restoreImagesModal}
        onClose={() => setRestoreImagesModal(false)}
        deletedImages={deletedImages}
        selectedImages={selectedImagesForRestore}
        onToggleSelection={handleToggleImageSelectionForRestore}
        onSelectAll={handleSelectAllForRestore}
        onRestoreSelected={handleRestoreSelectedImages}
      />
    </div>
  );
};

export default VariantsStep2;
